import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { formatRut, formatPhone } from '@/utils/formatters';

const initialFormState = {
    name: '',
    rut: '',
    email: '',
    phone: '',
    address: '',
    commune: ''
};

/**
 * CustomerFormDialog - Reusable dialog for creating/editing customers
 * 
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onOpenChange - Callback when dialog open state changes
 * @param {function} onSuccess - Callback when customer is created/updated successfully, receives customer data
 * @param {object} editingCustomer - Optional customer object for edit mode
 */
export default function CustomerFormDialog({
    open,
    onOpenChange,
    onSuccess,
    editingCustomer = null
}) {
    const { API } = useContext(AuthContext);
    const [formData, setFormData] = useState(initialFormState);
    const [submitting, setSubmitting] = useState(false);

    const isEditMode = !!editingCustomer;

    // Load editing customer data when provided
    useEffect(() => {
        if (editingCustomer) {
            setFormData({
                name: editingCustomer.name || '',
                rut: editingCustomer.rut || '',
                email: editingCustomer.email || '',
                phone: editingCustomer.phone || '',
                address: editingCustomer.address || '',
                commune: editingCustomer.commune || ''
            });
        } else {
            setFormData(initialFormState);
        }
    }, [editingCustomer, open]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            let response;
            if (isEditMode) {
                response = await axios.put(`${API}/customers/${editingCustomer.id}`, formData);
                toast.success('Cliente actualizado exitosamente');
            } else {
                response = await axios.post(`${API}/customers`, formData);
                toast.success('Cliente creado exitosamente');
            }

            setFormData(initialFormState);
            onOpenChange(false);

            if (onSuccess) {
                onSuccess(response.data);
            }
        } catch (error) {
            console.error('Customer form error:', error);
            toast.error(error.response?.data?.detail || (isEditMode ? 'Error al actualizar cliente' : 'Error al crear cliente'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle data-testid="customer-dialog-title">
                        {isEditMode ? 'Editar Cliente' : 'Crear Nuevo Cliente'}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditMode ? 'Modifica los datos del cliente' : 'Ingresa los datos del nuevo cliente'}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="customer-name" className="text-right">
                            Nombre *
                        </Label>
                        <Input
                            id="customer-name"
                            data-testid="customer-name-input"
                            className="col-span-3"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Razón Social"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="customer-rut" className="text-right">
                            RUT *
                        </Label>
                        <Input
                            id="customer-rut"
                            data-testid="customer-rut-input"
                            className="col-span-3"
                            value={formData.rut}
                            onChange={(e) => setFormData({ ...formData, rut: formatRut(e.target.value) })}
                            placeholder="76.xxx.xxx-x"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="customer-email" className="text-right">
                            Email
                        </Label>
                        <Input
                            id="customer-email"
                            type="email"
                            data-testid="customer-email-input"
                            className="col-span-3"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="contacto@empresa.cl"
                        />
                    </div>

                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="customer-phone" className="text-right">
                            Teléfono
                        </Label>
                        <Input
                            id="customer-phone"
                            data-testid="customer-phone-input"
                            className="col-span-3"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                            placeholder="+56 9 1234 5678"
                        />
                    </div>

                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="customer-address" className="text-right">
                            Dirección
                        </Label>
                        <Input
                            id="customer-address"
                            data-testid="customer-address-input"
                            className="col-span-3"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            placeholder="Av. Ejemplo 123"
                        />
                    </div>

                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="customer-commune" className="text-right">
                            Comuna
                        </Label>
                        <Input
                            id="customer-commune"
                            data-testid="customer-commune-input"
                            className="col-span-3"
                            value={formData.commune}
                            onChange={(e) => setFormData({ ...formData, commune: e.target.value })}
                            placeholder="Santiago"
                        />
                    </div>

                    <DialogFooter>
                        <Button
                            type="submit"
                            data-testid="submit-customer-button"
                            disabled={submitting}
                            className="w-full sm:w-auto"
                        >
                            {submitting ? 'Guardando...' : (isEditMode ? 'Actualizar Cliente' : 'Crear Cliente')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
